/**
 * Frontend Error Handler for Project Lighthouse
 * HIPAA-Compliant Healthcare Supervision Management System
 * 
 * Provides centralized error handling for React frontend with:
 * - API error processing and user-friendly messaging
 * - Offline error handling and queueing
 * - Component error boundaries integration
 * - User notification management
 * - Context-aware error recovery
 */

import toast from 'react-hot-toast';
import { 
  errorMessageService, 
  ErrorMessageService
} from './errorMessageService';
import { 
  UserType, 
  MessageContext 
} from './errorMessageTemplates';

// =============================================================================
// Frontend Error Types
// =============================================================================

export interface FrontendError {
  code: string;
  message: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  userMessage: string;
  retryable: boolean;
  context?: Record<string, any>;
  timestamp: Date;
  component?: string;
  action?: string;
  userId?: string;
  templateId?: string;
  actionSuggestions?: Array<{
    label: string;
    action: string;
    primary?: boolean;
  }>;
}

export interface ErrorResponse {
  code: string;
  message: string;
  severity: string;
  retryable: boolean;
  timestamp: string;
  requestId: string;
  context?: Record<string, any>;
}

export interface ErrorRecoveryAction {
  label: string;
  action: () => void;
  primary?: boolean;
}

// =============================================================================
// Error Categories and User Messages
// =============================================================================
// Note: Error messages are now handled by the errorMessageService with templates

// =============================================================================
// Frontend Error Handler Class
// =============================================================================

export class FrontendErrorHandler {
  private static instance: FrontendErrorHandler;
  private isOnline: boolean = navigator.onLine;
  private errorQueue: FrontendError[] = [];
  private retryTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private messageService: ErrorMessageService;
  private currentUserType: UserType = 'employee';
  private currentLocale: string = 'en';

  private constructor() {
    this.messageService = errorMessageService;
    this.setupOnlineHandling();
    this.setupGlobalErrorHandling();
    this.initializeUserContext();
  }

  /**
   * Initialize user context from session or localStorage
   */
  private initializeUserContext(): void {
    try {
      // Try to get user context from localStorage or session
      const userType = localStorage.getItem('userType') as UserType;
      const locale = localStorage.getItem('locale') || navigator.language.split('-')[0];
      
      if (userType) {
        this.setUserType(userType);
      }
      
      if (locale && ['en', 'es'].includes(locale)) {
        this.setLocale(locale);
      }
    } catch (error) {
      // Fallback to defaults if localStorage is not available
      console.warn('Unable to initialize user context from storage:', error);
    }
  }

  public static getInstance(): FrontendErrorHandler {
    if (!FrontendErrorHandler.instance) {
      FrontendErrorHandler.instance = new FrontendErrorHandler();
    }
    return FrontendErrorHandler.instance;
  }

  // =============================================================================
  // Public Configuration Methods
  // =============================================================================

  /**
   * Set the user type for contextualized error messages
   */
  public setUserType(userType: UserType): void {
    this.currentUserType = userType;
    this.messageService.setUserContext({ userType });
    
    try {
      localStorage.setItem('userType', userType);
    } catch (error) {
      console.warn('Unable to save user type to storage:', error);
    }
  }

  /**
   * Set the locale for internationalized error messages
   */
  public setLocale(locale: string): void {
    this.currentLocale = locale;
    this.messageService.updateConfig({ locale });
    
    try {
      localStorage.setItem('locale', locale);
    } catch (error) {
      console.warn('Unable to save locale to storage:', error);
    }
  }

  /**
   * Get current user type
   */
  public getUserType(): UserType {
    return this.currentUserType;
  }

  /**
   * Get current locale
   */
  public getLocale(): string {
    return this.currentLocale;
  }

  // =============================================================================
  // Public Error Handling Methods
  // =============================================================================

  /**
   * Handle API errors from backend responses
   */
  public handleAPIError(
    error: any,
    context?: { component?: string; action?: string; userId?: string }
  ): FrontendError {
    // Build message context
    const messageContext: Partial<MessageContext> = {
      userType: this.currentUserType,
      operation: context?.action || 'api_request',
      component: context?.component,
      timestamp: new Date()
    };

    // Process error using the new message service
    const processedMessage = this.messageService.processAPIError(error, messageContext);

    // Create frontend error with enhanced information
    const frontendError: FrontendError = {
      code: processedMessage.templateId || 'UNKNOWN_ERROR',
      message: processedMessage.message,
      severity: processedMessage.severity,
      userMessage: processedMessage.message,
      retryable: this.isRetryableError(error),
      context: { ...context },
      timestamp: processedMessage.timestamp,
      component: context?.component,
      action: context?.action,
      userId: context?.userId,
      templateId: processedMessage.templateId,
      actionSuggestions: processedMessage.actionSuggestions
    };

    this.processError(frontendError);
    return frontendError;
  }

  /**
   * Handle component errors (for error boundaries)
   */
  public handleComponentError(
    error: Error,
    errorInfo: { componentStack: string },
    componentName?: string
  ): FrontendError {
    const frontendError: FrontendError = {
      code: 'COMPONENT_ERROR',
      message: error.message,
      severity: 'high',
      userMessage: 'There was a problem displaying this content. Please refresh the page.',
      retryable: true,
      context: {
        componentName,
        componentStack: errorInfo.componentStack,
        stack: error.stack
      },
      timestamp: new Date(),
      component: componentName
    };

    this.processError(frontendError);
    return frontendError;
  }

  /**
   * Handle form validation errors
   */
  public handleValidationError(
    validationErrors: Record<string, string>,
    component?: string
  ): FrontendError {
    // Build message context
    const messageContext: Partial<MessageContext> = {
      userType: this.currentUserType,
      operation: 'form_validation',
      component: component
    };

    // Process validation errors using the new message service
    const processedMessages = this.messageService.processValidationErrors(validationErrors, messageContext);
    
    // Use the first processed message as the primary error
    const primaryMessage = processedMessages[0] || {
      message: 'Please correct the highlighted fields and try again.',
      severity: 'low' as const,
      category: 'validation',
      actionSuggestions: [],
      userType: this.currentUserType,
      timestamp: new Date()
    };

    const frontendError: FrontendError = {
      code: 'VALIDATION_ERROR',
      message: primaryMessage.message,
      severity: primaryMessage.severity,
      userMessage: primaryMessage.message,
      retryable: true,
      context: { validationErrors },
      timestamp: primaryMessage.timestamp,
      component,
      templateId: primaryMessage.templateId,
      actionSuggestions: primaryMessage.actionSuggestions
    };

    // Don't show toast for validation errors (handled by form UI)
    this.logError(frontendError);
    return frontendError;
  }

  /**
   * Handle offline storage errors
   */
  public handleStorageError(
    operation: string,
    error: Error,
    context?: Record<string, any>
  ): FrontendError {
    // Build message context
    const messageContext: Partial<MessageContext> = {
      userType: this.currentUserType,
      operation: 'storage_operation'
    };

    // Process error using the message service
    const processedMessage = this.messageService.processError('STORAGE_ERROR', messageContext, { operation });

    const frontendError: FrontendError = {
      code: 'STORAGE_ERROR',
      message: processedMessage.message,
      severity: processedMessage.severity,
      userMessage: processedMessage.message,
      retryable: true,
      context: { operation, originalError: error.message, ...context },
      timestamp: processedMessage.timestamp,
      templateId: processedMessage.templateId,
      actionSuggestions: processedMessage.actionSuggestions
    };

    this.processError(frontendError);
    return frontendError;
  }

  /**
   * Handle offline operation errors
   */
  public handleOfflineError(
    operation: string,
    context?: Record<string, any>
  ): FrontendError {
    // Build message context
    const messageContext: Partial<MessageContext> = {
      userType: this.currentUserType,
      operation: 'offline_operation'
    };

    // Process error using the message service
    const processedMessage = this.messageService.processError('OFFLINE_OPERATION_ERROR', messageContext, { operation });

    const frontendError: FrontendError = {
      code: 'OFFLINE_OPERATION_ERROR',
      message: processedMessage.message,
      severity: processedMessage.severity,
      userMessage: processedMessage.message,
      retryable: true,
      context: { operation, ...context },
      timestamp: processedMessage.timestamp,
      templateId: processedMessage.templateId,
      actionSuggestions: processedMessage.actionSuggestions
    };

    this.processError(frontendError);
    return frontendError;
  }

  // =============================================================================
  // Error Processing and User Feedback
  // =============================================================================

  /**
   * Process error - log, show notification, handle retry logic
   */
  private processError(error: FrontendError): void {
    // Log the error
    this.logError(error);

    // Queue error if offline
    if (!this.isOnline && error.retryable) {
      this.queueError(error);
    }

    // Show user notification (unless suppressed)
    if (!error.context?.suppressToast) {
      this.showUserNotification(error);
    }

    // Handle automatic retry logic
    if (error.retryable && this.shouldAutoRetry(error)) {
      this.scheduleRetry(error);
    }
  }

  /**
   * Show appropriate user notification
   */
  private showUserNotification(error: FrontendError): void {
    // Check if toast notifications should be suppressed
    if (error.context?.suppressToast) {
      return;
    }

    // Suppress connection/timeout toasts when the device is offline. The
    // offline badge in the header already communicates the state, so these
    // would just be noise (10+ stacked toasts when multiple background queries
    // fail in parallel). Genuine errors that happen while online are unaffected.
    if (
      (error.code === 'NETWORK_ERROR' || error.code === 'TIMEOUT_ERROR') &&
      !navigator.onLine
    ) {
      return;
    }

    const toastOptions = {
      duration: this.getNotificationDuration(error.severity),
      icon: this.getNotificationIcon(error.severity)
    };

    switch (error.severity) {
      case 'critical':
      case 'high':
        toast.error(error.userMessage, toastOptions);
        break;
      case 'medium':
        toast(error.userMessage, toastOptions);
        break;
      case 'low':
        // Don't show toast for low severity errors (like validation)
        break;
      case 'info':
        toast(error.userMessage, { ...toastOptions, icon: 'ℹ️' });
        break;
    }
  }

  /**
   * Log error to console and external services
   */
  private async logError(error: FrontendError): Promise<void> {
    const logData = {
      ...error,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: error.timestamp.toISOString()
    };

    // Log using the new logging system
    const frontendLogger = await import('./logger');
    const logger = frontendLogger.default;

    switch (error.severity) {
      case 'critical':
      case 'high':
        logger.error('🚨 Frontend Error', error.message, logData, 'ErrorHandler');
        break;
      case 'medium':
        logger.warn('⚠️ Frontend Warning', frontendLogger.LogCategory.ERROR, logData, 'ErrorHandler');
        break;
      case 'low':
      case 'info':
        logger.info('ℹ️ Frontend Info', frontendLogger.LogCategory.ERROR, logData, 'ErrorHandler');
        break;
    }

    // In production, send to error tracking service
    if (process.env.NODE_ENV === 'production') {
      this.sendToErrorTracking(error);
    }
  }

  // =============================================================================
  // Error Recovery and Retry Logic
  // =============================================================================

  /**
   * Provide recovery actions for errors
   */
  public getRecoveryActions(error: FrontendError): ErrorRecoveryAction[] {
    const actions: ErrorRecoveryAction[] = [];

    switch (error.code) {
      case 'AUTH_EXPIRED':
      case 'AUTH_INVALID':
        actions.push({
          label: 'Log In Again',
          action: () => window.location.href = '/login',
          primary: true
        });
        break;

      case 'NETWORK_ERROR':
      case 'TIMEOUT_ERROR':
        actions.push({
          label: 'Retry',
          action: () => window.location.reload(),
          primary: true
        });
        break;

      case 'COMPONENT_ERROR':
        actions.push({
          label: 'Refresh Page',
          action: () => window.location.reload(),
          primary: true
        });
        break;

      case 'STORAGE_ERROR':
        actions.push({
          label: 'Clear Local Data',
          action: () => this.clearLocalStorage(),
        });
        break;

      default:
        if (error.retryable) {
          actions.push({
            label: 'Try Again',
            action: () => window.location.reload(),
            primary: true
          });
        }
        break;
    }

    // Always provide option to contact support for high/critical errors
    if (error.severity === 'critical' || error.severity === 'high') {
      actions.push({
        label: 'Contact Support',
        action: async () => {
          // In real implementation, this would open support chat or email
          // Log user contact for support
        const frontendLogger = await import('./logger');
        frontendLogger.default.userAction('User contacted support for error', { errorCode: error.code }, 'ErrorHandler');
        }
      });
    }

    return actions;
  }

  /**
   * Schedule automatic retry for retryable errors
   */
  private scheduleRetry(error: FrontendError): void {
    if (this.retryTimeouts.has(error.code)) {
      return; // Already scheduled
    }

    const retryDelay = this.calculateRetryDelay(error);
    const timeoutId = setTimeout(() => {
      this.retryTimeouts.delete(error.code);
      this.handleRetry(error);
    }, retryDelay);

    this.retryTimeouts.set(error.code, timeoutId);
  }

  /**
   * Handle error retry
   */
  private handleRetry(error: FrontendError): void {
    if (error.code === 'NETWORK_ERROR' && this.isOnline) {
      // Network is back, process queued errors
      this.processQueuedErrors();
    }
    // Other retry logic would depend on specific error types
  }

  // =============================================================================
  // Helper Methods
  // =============================================================================

  private createNetworkError(context?: Record<string, any>): FrontendError {
    // Build message context
    const messageContext: Partial<MessageContext> = {
      userType: this.currentUserType,
      operation: 'network_request'
    };

    // Process error using the message service
    const processedMessage = this.messageService.processError('SYS_CONNECTION_ERROR', messageContext);

    return {
      code: 'NETWORK_ERROR',
      message: processedMessage.message,
      severity: processedMessage.severity,
      userMessage: processedMessage.message,
      retryable: true,
      context,
      timestamp: processedMessage.timestamp,
      templateId: processedMessage.templateId,
      actionSuggestions: processedMessage.actionSuggestions
    };
  }

  private createTimeoutError(context?: Record<string, any>): FrontendError {
    // Build message context
    const messageContext: Partial<MessageContext> = {
      userType: this.currentUserType,
      operation: 'timeout_request'
    };

    // Process error using the message service
    const processedMessage = this.messageService.processError('SYS_CONNECTION_ERROR', messageContext);

    return {
      code: 'TIMEOUT_ERROR',
      message: processedMessage.message,
      severity: processedMessage.severity,
      userMessage: processedMessage.message,
      retryable: true,
      context,
      timestamp: processedMessage.timestamp,
      templateId: processedMessage.templateId,
      actionSuggestions: processedMessage.actionSuggestions
    };
  }

  private createUnknownError(error: any, context?: Record<string, any>): FrontendError {
    // Build message context
    const messageContext: Partial<MessageContext> = {
      userType: this.currentUserType,
      operation: 'unknown_operation'
    };

    // Process error using the message service
    const processedMessage = this.messageService.processError('SYS_CONNECTION_ERROR', messageContext);

    return {
      code: 'UNKNOWN_ERROR',
      message: processedMessage.message,
      severity: processedMessage.severity,
      userMessage: processedMessage.message,
      retryable: true,
      context: { originalError: error, ...context },
      timestamp: processedMessage.timestamp,
      templateId: processedMessage.templateId,
      actionSuggestions: processedMessage.actionSuggestions
    };
  }

  /**
   * Determine if an error is retryable based on error characteristics
   */
  private isRetryableError(error: any): boolean {
    // Network errors are generally retryable
    if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
      return true;
    }

    // Timeout errors are retryable
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return true;
    }

    // Check HTTP status codes
    if (error.response?.status) {
      const status = error.response.status;
      // Retryable status codes
      const retryableStatuses = [408, 429, 500, 502, 503, 504];
      return retryableStatuses.includes(status);
    }

    // Check backend error response
    if (error.response?.data?.retryable !== undefined) {
      return error.response.data.retryable;
    }

    // Default to retryable for unknown errors
    return true;
  }

  private getNotificationDuration(severity: string): number {
    switch (severity) {
      case 'critical':
      case 'high':
        return 8000;
      case 'medium':
        return 5000;
      case 'low':
        return 3000;
      case 'info':
        return 4000;
      default:
        return 5000;
    }
  }

  private getNotificationIcon(severity: string): string {
    switch (severity) {
      case 'critical':
      case 'high':
        return '🚨';
      case 'medium':
        return '⚠️';
      case 'low':
        return '💡';
      case 'info':
        return 'ℹ️';
      default:
        return '❗';
    }
  }

  private shouldAutoRetry(error: FrontendError): boolean {
    const autoRetryableCodes = ['NETWORK_ERROR', 'TIMEOUT_ERROR'];
    return autoRetryableCodes.includes(error.code);
  }

  private calculateRetryDelay(error: FrontendError): number {
    // Exponential backoff based on error type
    switch (error.code) {
      case 'NETWORK_ERROR':
        return 5000; // 5 seconds
      case 'TIMEOUT_ERROR':
        return 10000; // 10 seconds
      default:
        return 3000; // 3 seconds
    }
  }

  private queueError(error: FrontendError): void {
    this.errorQueue.push(error);
    // Limit queue size
    if (this.errorQueue.length > 50) {
      this.errorQueue.shift();
    }
  }

  private async processQueuedErrors(): Promise<void> {
    const errors = [...this.errorQueue];
    this.errorQueue = [];
    
    for (const error of errors) {
      // Log error processing
      const frontendLogger = await import('./logger');
      frontendLogger.default.debug('Processing queued error', frontendLogger.LogCategory.ERROR, { errorCode: error.code }, 'ErrorHandler');
      // Specific retry logic would depend on error type
    }
  }

  private clearLocalStorage(): void {
    try {
      localStorage.clear();
      sessionStorage.clear();
      toast.success('Local data cleared successfully');
    } catch (error) {
      toast.error('Failed to clear local data');
    }
  }

  private async sendToErrorTracking(error: FrontendError): Promise<void> {
    // In production, this would send to services like:
    // - Sentry
    // - LogRocket
    // - Bugsnag
    // - Custom error tracking endpoint
    // Log error tracking simulation
    const frontendLogger = await import('./logger');
    frontendLogger.default.debug('Would send to error tracking', frontendLogger.LogCategory.ERROR, { errorCode: error.code }, 'ErrorHandler');
  }

  private setupOnlineHandling(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      toast.success('Connection restored');
      this.processQueuedErrors();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      toast('You are now offline. Changes will be saved locally.', {
        icon: '📱',
        duration: 3000
      });
    });
  }

  private setupGlobalErrorHandling(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = this.handleAPIError(event.reason, {
        component: 'global',
        action: 'unhandledrejection'
      });
      
      // Prevent default browser error handling for known errors
      if (error.code !== 'UNKNOWN_ERROR') {
        event.preventDefault();
      }
    });

    // Handle JavaScript errors
    window.addEventListener('error', (event) => {
      this.handleComponentError(event.error || new Error(event.message), {
        componentStack: `at ${event.filename}:${event.lineno}:${event.colno}`
      }, 'global');
    });
  }
}

// =============================================================================
// Convenience Functions
// =============================================================================

export const errorHandler = FrontendErrorHandler.getInstance();

export function handleAPIError(error: any, context?: { component?: string; action?: string }) {
  return errorHandler.handleAPIError(error, context);
}

export function handleComponentError(error: Error, errorInfo: any, componentName?: string) {
  return errorHandler.handleComponentError(error, errorInfo, componentName);
}

export function handleValidationError(errors: Record<string, string>, component?: string) {
  return errorHandler.handleValidationError(errors, component);
}

export function handleStorageError(operation: string, error: Error, context?: Record<string, any>) {
  return errorHandler.handleStorageError(operation, error, context);
}

export function handleOfflineError(operation: string, context?: Record<string, any>) {
  return errorHandler.handleOfflineError(operation, context);
}

// =============================================================================
// Enhanced Error Handling Functions
// =============================================================================

/**
 * Set user type for contextualized error messages
 */
export function setUserType(userType: UserType): void {
  errorHandler.setUserType(userType);
}

/**
 * Set locale for internationalized error messages
 */
export function setLocale(locale: string): void {
  errorHandler.setLocale(locale);
}

/**
 * Get user-friendly message for error code
 */
export function getUserFriendlyMessage(
  errorCode: string,
  context?: { component?: string; action?: string },
  variables?: Record<string, any>
): string {
  const messageContext: Partial<MessageContext> = {
    userType: errorHandler.getUserType(),
    operation: context?.action || 'unknown',
    component: context?.component
  };
  
  return errorMessageService.getUserMessage(errorCode, messageContext, variables);
}

/**
 * Get action suggestions for an error
 */
export function getErrorActionSuggestions(
  errorCode: string,
  context?: { component?: string; action?: string }
): Array<{ label: string; action: string; primary?: boolean }> {
  const messageContext: Partial<MessageContext> = {
    userType: errorHandler.getUserType(),
    operation: context?.action || 'unknown',
    component: context?.component
  };
  
  return errorMessageService.getActionSuggestions(errorCode, messageContext);
}

export default FrontendErrorHandler;